import { useState, useMemo } from 'react';
import useCategorias from '../../hooks/useCategorias';
import useConfiguracion from '../../hooks/useConfiguracion';
import { textoPaso } from '../../utils/textosReporte';

// Íconos de respaldo por palabra clave del nombre — antes toda categoría sin
// `cat_fileLogo` propio mostraba el mismo triángulo de advertencia genérico,
// lo que se veía pobre apenas había más de 2-3 categorías. El municipio
// puede subir su propio ícono desde Configuración; mientras no lo haga, esto
// da una pista visual real en vez de un genérico repetido.
const ICONOS_POR_PALABRA_CLAVE = [
  [/ruido/i, 'fa-volume-high'],
  [/alumbrado|luz|foco|poste/i, 'fa-lightbulb'],
  [/bache|pista|pavimento|via\b/i, 'fa-road'],
  [/sem[aá]foro/i, 'fa-traffic-light'],
  [/agua|fuga|desag[uü]e|alcantarill/i, 'fa-faucet-drip'],
  [/basura|residuo|limpieza/i, 'fa-trash'],
  [/seguridad|robo|asalto/i, 'fa-shield-halved'],
  [/[aá]rbol|poda|jard[ií]n|parque/i, 'fa-tree'],
  [/animal|perro|gato/i, 'fa-paw'],
  [/tr[aá]nsito|veh[ií]culo|auto|moto/i, 'fa-car'],
  [/ruta|se[nñ]al[ei]/i, 'fa-signs-post'],
];

const iconoPorNombre = (nombre = '') => {
  const coincidencia = ICONOS_POR_PALABRA_CLAVE.find(([patron]) => patron.test(nombre));
  return coincidencia ? coincidencia[1] : 'fa-triangle-exclamation';
};

/**
 * Paso 1/5 del reporte: elegir categoría (README diseño, sección 5.2).
 * Prioridad de ícono (HU-009-C): imagen propia (`cat_fileLogo`) > ícono de
 * librería elegido en el panel (`cat_chIconoPreset`) > adivinado por
 * palabra clave del nombre (`iconoPorNombre`, última red de contención para
 * que nunca se vea el mismo genérico repetido en todas las categorías).
 */
const PasoCategoria = ({ categoriaId, onSeleccionar, onContinuar }) => {
  const { categorias, loading } = useCategorias();
  const { configuracion } = useConfiguracion();
  const [busqueda, setBusqueda] = useState('');
  const texto = textoPaso(configuracion, 'categoria', {
    titulo: '¿Qué pasó?',
    subtitulo: 'Elige la categoría que mejor describe lo que viste.',
  });

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return categorias;
    return categorias.filter((c) => c.cat_chNombre.toLowerCase().includes(q));
  }, [categorias, busqueda]);

  return (
    <div className="rw-paso">
      <h2 className="rw-titulo" style={texto.estilo}>{texto.titulo}</h2>
      <p className="rw-subtitulo" style={texto.estilo}>{texto.subtitulo}</p>

      <input
        type="text"
        className="rw-input-buscar"
        placeholder={`Buscar entre ${categorias.length} categorías…`}
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
      />

      <div className="rw-categorias-grid">
        {loading && <p className="rw-texto-ayuda">Cargando categorías…</p>}
        {!loading && filtradas.length === 0 && (
          <p className="rw-texto-ayuda">No hay categorías que coincidan con "{busqueda}".</p>
        )}
        {filtradas.map((cat) => (
          <button
            key={cat.cat_inId}
            type="button"
            className={`rw-categoria-card ${categoriaId === cat.cat_inId ? 'rw-categoria-activa' : ''}`}
            onClick={() => onSeleccionar(cat.cat_inId)}
          >
            {cat.cat_fileLogo
              ? <img src={cat.cat_fileLogo} alt="" className="rw-categoria-logo" />
              : <i className={`fas ${cat.cat_chIconoPreset || iconoPorNombre(cat.cat_chNombre)} rw-categoria-icono`}></i>}
            <span>{cat.cat_chNombre}</span>
          </button>
        ))}
      </div>

      <div className="rw-pie-fijo">
        <button
          type="button"
          className="rw-boton-primario"
          disabled={!categoriaId}
          onClick={onContinuar}
        >
          Continuar
        </button>
      </div>
    </div>
  );
};

export default PasoCategoria;
